import { useState } from 'react';
import { Settings, Moon, Sun, Monitor, Bell, Shield, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useUIStore } from '@/stores/uiStore';
import { useToast } from '@/hooks/useToast';

export default function SettingsPage() {
  const { theme, setTheme } = useUIStore();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState(true);

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    toast({
      title: '테마 변경',
      description: `테마가 ${newTheme === 'light' ? '라이트' : newTheme === 'dark' ? '다크' : '시스템'}로 변경되었습니다.`,
    });
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Settings className="h-8 w-8" />
          설정
        </h1>
        <p className="text-muted-foreground mt-1">앱 환경설정을 관리합니다.</p>
      </div>

      <div className="space-y-6">
        {/* Appearance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              테마
            </CardTitle>
            <CardDescription>앱의 외관을 설정합니다.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button
                variant={theme === 'light' ? 'default' : 'outline'}
                className="flex-1 flex items-center justify-center gap-2"
                onClick={() => handleThemeChange('light')}
              >
                <Sun className="h-4 w-4" />
                라이트
              </Button>
              <Button
                variant={theme === 'dark' ? 'default' : 'outline'}
                className="flex-1 flex items-center justify-center gap-2"
                onClick={() => handleThemeChange('dark')}
              >
                <Moon className="h-4 w-4" />
                다크
              </Button>
              <Button
                variant={theme === 'system' ? 'default' : 'outline'}
                className="flex-1 flex items-center justify-center gap-2"
                onClick={() => handleThemeChange('system')}
              >
                <Monitor className="h-4 w-4" />
                시스템
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              알림
            </CardTitle>
            <CardDescription>알림 설정을 관리합니다.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="push-notifications">푸시 알림</Label>
                <p className="text-sm text-muted-foreground">
                  새로운 메시지나 업데이트에 대한 알림을 받습니다.
                </p>
              </div>
              <Switch
                id="push-notifications"
                checked={notifications}
                onCheckedChange={setNotifications}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="email-notifications">이메일 알림</Label>
                <p className="text-sm text-muted-foreground">
                  중요한 업데이트를 이메일로 받습니다.
                </p>
              </div>
              <Switch id="email-notifications" defaultChecked={false} />
            </div>
          </CardContent>
        </Card>

        {/* Privacy & Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              개인정보 및 보안
            </CardTitle>
            <CardDescription>개인정보 및 보안 설정을 관리합니다.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="two-factor">2단계 인증</Label>
                <p className="text-sm text-muted-foreground">
                  계정 보안을 위해 2단계 인증을 활성화합니다.
                </p>
              </div>
              <Switch id="two-factor" defaultChecked={false} />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="activity-log">활동 로그</Label>
                <p className="text-sm text-muted-foreground">
                  계정 활동 기록을 저장합니다.
                </p>
              </div>
              <Switch id="activity-log" defaultChecked />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
